import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment, { duration, DurationFormatSettings } from 'moment';
import { FormBuilder, FormGroup } from '@angular/forms';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DurationValue, FilterDuration } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-duration.model';
import { FilterBase } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-base';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { createDurationRangeValidator } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/duration-validator';
import { NimbusFilterDurationRangeInputComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-duration-range-input/nimbus-filter-duration-range-input.component';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-filter-duration-range',
  templateUrl: './nimbus-filter-duration-range.component.html',
  styleUrls: ['./nimbus-filter-duration-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterDurationRangeComponent extends FilterBase<DurationValue, FilterDuration> {
  @ViewChild('startControl')
  public startControl: NimbusFilterDurationRangeInputComponent;
  @ViewChild('endControl')
  public endControl: NimbusFilterDurationRangeInputComponent;

  private readonly formatSettings: DurationFormatSettings = { forceLength: true, trim: false };
  protected valuesEmpty: DurationValue[] = [
    { start: null, end: null },
    { start: '00:00:00', end: '00:00:00' },
  ];
  public readonly durationFormat: string = 'HH:mm:ss';

  public form: FormGroup;
  public hasError: boolean = false;
  public constructor(
    private readonly translateService: TranslateService,
    formBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();

    this.form = formBuilder.group(
      {
        min: [
          duration({
            hours: 0,
            minutes: 0,
            seconds: 0,
          }),
        ],
        max: [
          duration({
            hours: 0,
            minutes: 0,
            seconds: 0,
          }),
        ],
      },
      {
        validators: [createDurationRangeValidator()],
      },
    );
  }

  public onAttach(): void {
    of({})
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      .pipe(delay(100), untilDestroyed(this))
      .subscribe(() => {
        this.startControl?.onFocusIn();
      });
  }

  public onDetach(): void {
    this.filterValue = {
      start: this.form.getRawValue().min.format(this.durationFormat, this.formatSettings),
      end: this.form.getRawValue().max.format(this.durationFormat, this.formatSettings),
    };
    this.filterValueChanged.emit(this.filterValue);
    this.summary = this.getSummary();
    this.changeDetectorRef.detectChanges();
  }

  protected getSummary(): string {
    const max = duration(this.filterValue?.end);
    const min = duration(this.filterValue?.start);
    if (!this.filterValue || (!max.asMilliseconds() && !min.asMilliseconds())) {
      this.hasError = false;
      return this.translateService.instant(TranslationKeys.FilterAny);
    }

    this.form.updateValueAndValidity();
    this.hasError = this.form.invalid;
    if (this.form.invalid) {
      return `Invalid Duration Range Selected`;
    }

    return min.asMilliseconds()
      ? `${min.format(this.durationFormat, this.formatSettings)} - ${max.format(this.durationFormat, this.formatSettings)}`
      : `00:00:00 - ${max.format(this.durationFormat, this.formatSettings)}`;
  }

  protected onFilterValueChanged(): void {
    const start = moment.duration(this.filterValue?.start || '00:00:00');
    const end = moment.duration(this.filterValue?.end || '00:00:00');
    this.form.patchValue({
      min: duration({
        hours: Math.floor(start.asHours()),
        minutes: start.minutes(),
        seconds: start.seconds(),
      }),
      max: duration({
        hours: Math.floor(end.asHours()),
        minutes: end.minutes(),
        seconds: end.seconds(),
      }),
    });
  }
}
